import { recipe, splitRecipeProps, type RecipeVariantProps } from "dx-styles"
import type { ReactElement } from "react"

import { bodyTextStyle, tokens } from "../theme"
import type { ElementProps } from "../types"

export const textRecipe = recipe({
  // The body style in the base is what makes `body` both the default and a
  // reset: every Text starts from body values, and the other variants override
  // only what they change. That is why `body` below is empty.
  base: { ...bodyTextStyle, margin: 0 },
  variants: {
    variant: {
      body: {},
      // Colour shifts on body copy only — headings carry their own weight, so
      // there is no de-emphasised or on-dark heading to express.
      description: { color: tokens.color.textDescription },
      onDark: { color: tokens.color.textOnDark },
      danger: { color: tokens.color.error },
      h3: {
        fontSize: tokens.fontSize.h3,
        lineHeight: tokens.lineHeight.normal,
        fontWeight: tokens.fontWeight.semibold,
      },
      h2: {
        fontSize: tokens.fontSize.h2,
        lineHeight: tokens.lineHeight.normal,
        fontWeight: tokens.fontWeight.semibold,
      },
      h1: {
        fontStyle: "italic",
        fontFamily: tokens.font.title,
        fontSize: tokens.fontSize.h1,
        lineHeight: tokens.lineHeight.tight,
        fontWeight: tokens.fontWeight.regular,
      },
    },
    /**
     * Text alignment. Its own axis because, unlike `description`/`onDark`, it is
     * genuinely orthogonal to the ramp — any heading or body copy can be centred.
     *
     * Logical values (`start`/`end`, not `left`/`right`) so the alignment follows
     * the writing direction. Deliberately no entry in `defaultVariants`: with no
     * value selected no class is emitted, so text inherits its container's
     * alignment instead of being forced back to the start.
     */
    align: {
      start: { textAlign: "start" },
      center: { textAlign: "center" },
      end: { textAlign: "end" },
    },
    whiteSpace: {
      breakSpace: {
        whiteSpace: "break-spaces",
      },
      noWrap: {
        whiteSpace: "nowrap",
      },
    },
  },
  defaultVariants: {
    variant: "body",
  },
})

type TextVariantProps = RecipeVariantProps<typeof textRecipe>
type TextVariant = NonNullable<TextVariantProps["variant"]>

/**
 * The variant fully determines the element — there is no `render` override. The
 * type ramp and the document outline stay in step by construction, so a page
 * cannot end up with an `<h3>` wearing `h2` styling.
 */
const TAG_FOR_VARIANT: Record<TextVariant, "h1" | "h2" | "h3" | "p"> = {
  body: "p",
  description: "p",
  onDark: "p",
  danger: "p",
  h1: "h1",
  h2: "h2",
  h3: "h3",
}

/**
 * The DOM props are `<p>`'s. The variant may render an `<h1>`–`<h3>` instead,
 * but the four attribute sets are the same, so this is the whole surface —
 * including a `ref`, which every one of the four accepts.
 */
export type TextProps = TextVariantProps &
  Omit<ElementProps<"p">, keyof TextVariantProps>

export const Text = (props: TextProps): ReactElement => {
  const { otherProps, variantProps } = splitRecipeProps(textRecipe, props)
  const Tag = TAG_FOR_VARIANT[variantProps.variant ?? "body"]

  return (
    <Tag
      {...otherProps}
      className={textRecipe(variantProps)}
    />
  )
}
